import { Alignment, Fit, Layout, type Rive } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { type CSSProperties, memo, type ReactNode, useEffect, useMemo, useRef } from 'react';

import { RIVE_CONTAINER_DEFAULT_ERROR } from '~/constants/testIDs';
import { useRiveWithStateMachines } from '~/hooks/useRiveWithStateMachines';

export type RiveContainerProps = {
  src: string;
  className?: string;
  style?: CSSProperties;
  errorFallback?: ReactNode;
  onRiveReady?: (rive: Rive) => void;
  onLoadError?: () => void;
  resizeDependencies?: number[];
  'data-testid'?: string;
};

export const RiveContainer = memo(
  ({
    src,
    className = 'size-full',
    style,
    errorFallback,
    onRiveReady,
    onLoadError,
    resizeDependencies = [],
    'data-testid': dataTestId,
  }: RiveContainerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Create layout inside component to avoid SSR issues
    const riveLayout = useMemo(
      () =>
        new Layout({
          fit: Fit.Contain, // Like object-fit: contain
          alignment: Alignment.Center,
        }),
      [],
    );

    // Use the custom hook for Rive with state machine detection
    const { RiveComponent, rive, setContainerRef, hasError } = useRiveWithStateMachines({
      src,
      autoplay: true,
      layout: riveLayout,
      onLoadError,
    });

    // Set container ref initially
    useEffect(() => {
      // biome-ignore lint/suspicious/noUnnecessaryConditions: the container ref is null until the div mounts, so this read stays even though the ref type looks always empty here
      if (containerRef.current) {
        setContainerRef(containerRef.current);
      }
    }, [setContainerRef]);

    // Use ResizeObserver for consistent container size detection
    useEffect(() => {
      // biome-ignore lint/suspicious/noUnnecessaryConditions: rive and the container ref are both populated by the time this effect runs, and the early return stays
      if (!containerRef.current || !rive) {
        return;
      }

      const resizeObserver = new ResizeObserver(() => {
        if (rive) {
          // Force immediate resize when container size changes
          rive.resizeDrawingSurfaceToCanvas();
        }
      });

      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [rive]);

    // Additional trigger for gallery dependency changes
    useEffect(() => {
      if (!rive || resizeDependencies.length === 0) {
        return;
      }

      // Trigger resize when dependencies change
      rive.resizeDrawingSurfaceToCanvas();
    }, [rive, resizeDependencies]);

    // Notify parent when Rive instance is ready
    useEffect(() => {
      if (rive && onRiveReady) {
        onRiveReady(rive);
      }
    }, [rive, onRiveReady]);

    if (hasError) {
      return (
        <div
          ref={containerRef}
          className="flex items-center justify-center ads:bg-subtle bg-gray-3 ads:text-metadata text-gray-8"
          style={style}
        >
          {errorFallback || (
            <span data-testid={RIVE_CONTAINER_DEFAULT_ERROR} className="text-12">
              Error loading animation
            </span>
          )}
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        className={classNames(className, 'relative overflow-hidden')}
        style={style}
        data-testid={dataTestId}
      >
        <RiveComponent />
      </div>
    );
  },
);

RiveContainer.displayName = 'RiveContainer';
